import { Theme, Typography } from '@mui/material'
import { LocalizedTypography } from 'components/Localization/LocalizedTypography'
import { matchSorter } from 'match-sorter'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { makeStyles } from 'tss-react/mui'
import { fuzzySearchMultipleWords, markText } from 'utils/textFilterUtils'
import { MaterialSummaryDto } from '../../../services/APIs/InternalAPI/internal-api.contracts'

import { SearchTextField } from 'components/OrganizationSettings/VirtualFactory/Capabilities/WorkingStepsSurchargesAndDiscounts/components/SearchTextField'
import { isEqual } from 'lodash'
import './AvailableArticles.sass'

export type AvailableArticlesProps = {
  materials: MaterialSummaryDto[]
  selectedMaterial?: MaterialSummaryDto
  onMaterialSelected: (
    material: MaterialSummaryDto,
    shouldSave?: boolean,
  ) => void
  width: string
  maxHeight?: string
}

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    height: '100%',
  },
  searchField: {
    padding: theme.spacing(1),
    display: 'flex',
    justifyContent: 'flex-end',
  },
  optionList: {
    height: '100%',
    overflow: 'auto',
  },
  optionRoot: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1),
    cursor: 'pointer',
    '& mark': {
      backgroundColor: theme.palette.warning.light,
      color: theme.palette.warning.contrastText,
    },
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  selectedOption: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
    },
    '& span': {
      color: theme.palette.primary.contrastText,
    },
  },
}))

const filterOptions = (
  options: Array<MaterialSummaryDto>,
  currentFilter: string,
): MaterialSummaryDto[] => {
  if (!options?.length) return []

  return fuzzySearchMultipleWords(
    options,
    [
      { key: 'id', minRanking: matchSorter.rankings.CONTAINS },
      { key: 'description', threshold: matchSorter.rankings.CONTAINS },
    ],
    currentFilter,
  )
}

// const debouncedSearch = _.debounce(filterOptions, 500)

const AvailableArticles = (props: AvailableArticlesProps) => {
  const { classes, cx } = useStyles()

  const { t } = useTranslation()
  const [showMark, setShowMark] = React.useState(false)

  const [currentFilter, setCurrentFilter] = React.useState('')

  const filteredMaterials = React.useMemo(() => {
    if (currentFilter) {
      return filterOptions(props.materials, currentFilter)
    }
    return props.materials
  }, [props.materials, currentFilter])

  const listRef = React.useRef<HTMLUListElement>(null)
  const selectedArticleRef = React.useRef<HTMLLIElement>(null)

  useEffect(() => {
    if (selectedArticleRef.current) {
      selectedArticleRef.current.scrollIntoView({
        behavior: 'instant',
        block: 'nearest',
      })
      return
    }
  }, [filteredMaterials, props.selectedMaterial?.id])

  return (
    <div style={{ width: props.width, height: '100%' }}>
      {props.materials?.length > 0 ? (
        <div className={classes.root}>
          <div className={classes.searchField}>
            <SearchTextField
              currentQuery={currentFilter}
              updateQuery={setCurrentFilter}
              fullWidth
              placeholder={t(
                'project:available-articles',
                'available articles',
              ).toString()}
            />
          </div>
          <ul className={classes.optionList} ref={listRef}>
            {filteredMaterials.map((material) => {
              return (
                <li
                  key={material.id}
                  aria-selected={props.selectedMaterial?.id === material.id}
                  data-materialid={material.id}
                  ref={
                    props.selectedMaterial?.id === material.id
                      ? selectedArticleRef
                      : null
                  }
                  className={cx(classes.optionRoot, {
                    [classes.selectedOption]:
                      props.selectedMaterial?.id === material.id,
                  })}
                  onClick={() => {
                    props.onMaterialSelected(material)
                  }}
                >
                  <Typography variant="body2">
                    {markText(material.description, currentFilter)}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {material.id}
                  </Typography>
                </li>
              )
            })}
          </ul>
        </div>
      ) : (
        <NoAvailableMaterials />
      )}
    </div>
  )
}

const AvailableArticlesMemoized = React.memo(AvailableArticles, isEqual)

export default AvailableArticlesMemoized

const NoAvailableMaterials = () => {
  return (
    <div className="no-available-materials-container">
      <LocalizedTypography
        translationKey={'project:article-selector--no-available-articles'}
      >
        no articles found with the given keywords
      </LocalizedTypography>
    </div>
  )
}
